import React, { ReactNode } from 'react';

import { formatDistance } from 'date-fns';
import { generatePath, NavLink } from 'react-router-dom';

import { Avatar } from '@/components/Avatar';
import { User } from '@/core/api/collections/users';
import { LicenseTypeSelectContainer } from '@/core/users/UserPermissions/UserDetails/LicenseTypeSelectContainer';
import { UserDetailsName } from '@/core/users/UserPermissions/UserDetails/UserDetailsName';
import routes from '@/routes';

type Props = {
  user: User;
  actionButton?: ReactNode;
  resendInvitationButton?: ReactNode;
}
export function UserDetails ({ actionButton, resendInvitationButton, user }: Props) {
  const { teams } = user;
  const bothButtonsShown = actionButton && resendInvitationButton;

  return (
    <>
      <div
        className="flex items-center justify-center bg-gray-100 rounded-full"
        style={{ boxShadow: '4px 4px 7px rgba(110, 163, 238, 0.29)', width: '3.25rem', height: '3.25rem' }}
      >
        <div className="w-10 h-10">
          <Avatar user={user} />
        </div>
      </div>
      <div className={`max-w-full ${bothButtonsShown ? 'col-span-3' : 'col-span-4'}`}>
        <UserDetailsName user={user} />
      </div>
      <div className={`py-2 ${bothButtonsShown ? 'col-span-2' : undefined}`}>
        <div className="flex">
          { resendInvitationButton }
          { actionButton }
        </div>
      </div>

      <div className="col-start-2 col-span-2">
        <LicenseTypeSelectContainer />
      </div>
      <UserDetailsDataEntry className="col-start-4" title="Product access">
        {[
          (user.permissions.core?.state !== 'deleted' && user.permissions.core) ? 'UserVoice' : [],
          (user.permissions.uvk?.state !== 'deleted' && user.permissions.uvk?.admin) ? 'Validation' : [],
        ].flat().join(', ') || '--'}
      </UserDetailsDataEntry>
      <UserDetailsDataEntry className="col-start-5" title="Team">
        {teams.length > 1 && (
          <NavLink
            replace
            className="text-link"
            to={generatePath(routes.users.permissions, { userId: user.id, tab: 'teams' })}
          >
            ({teams.length} teams)
          </NavLink>
        )}
        {teams.length === 1 && teams[0].name}
        {teams.length === 0 && '--'}
      </UserDetailsDataEntry>
      <UserDetailsDataEntry className="col-start-6" title="Last active">
        {user.lastLogin ? formatDistance(new Date(user.lastLogin), new Date(), { addSuffix: true }) : '--'}
      </UserDetailsDataEntry>
    </>
  );
}

type UserDetailsDataEntryProps = {
  title?: string;
  className: string;
  children: ReactNode;
}
function UserDetailsDataEntry ({ title, className, children }: UserDetailsDataEntryProps) {
  return (
    <div className={className}>
      <div className="font-medium">{ title }</div>
      <div className="mt-2 font-light truncate">{ children }</div>
    </div>
  );
}

